<template>
  <vs-text class="text-break">{{ valueAsString }}</vs-text>
</template>

<script setup lang="ts">
import { useGetters } from 'vuex-composition-helpers'
import { formatDateTime, formatDateWithYear } from '@/utils/date-formatting'
import { ref, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import moment from 'moment'
import { getById } from '@/api/locations'
import { useInventoryTypes } from '@/composables/inventory-types/useInventoryTypes'

const props = defineProps<{
  lookup: object & { entity?: string }
  format: string
  value: string | number | boolean
}>()

const { getBySid: getUserBySid } = useGetters('users', ['getBySid'])
const { getById: getCompanyById } = useGetters('companies', ['getById'])
const { getBusinessUnitById: getBusinessById } = useGetters('businessUnits', [
  'getBusinessUnitById',
])
const { getInventorySubTypeById, getInventoryTypeOfSubTypeId, isInitialLoading } =
  useInventoryTypes()
const queryClient = useQueryClient()

const getLocationById = (id) => {
  return queryClient.fetchQuery({
    queryKey: ['locations', id],
    queryFn: ({ signal }) => getById(id, signal),
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
  })
}

const valueAsString = ref<string>('')

const calculateStringValue = async (): Promise<string> => {
  if (props.value === true) {
    return 'Yes'
  } else if (props.value === false) {
    return 'No'
  }
  switch (props.lookup.entity) {
    case 'user':
      return getUserBySid.value(props.value)?.name ?? 'Unknown user'
    case 'company':
      return getCompanyById.value(props.value)?.name ?? 'Unknown company'
    case 'location':
      return (await getLocationById(props.value)).location ?? 'Unknown location'
    case 'business_unit':
      return getBusinessById.value(props.value)?.name ?? 'Unknown business unit'
    case 'inventory_sub_type':
      const valueAsNumber = typeof props.value === 'number' ? props.value : parseInt(props.value)
      return (
        `${getInventorySubTypeById(valueAsNumber)?.name ?? 'Unknown Inventory Item'} · ` +
        (getInventoryTypeOfSubTypeId(valueAsNumber)?.name ?? 'Unknown Inventory Type')
      )
    default:
      switch (props.format) {
        case 'date':
          return formatDateWithYear(props.value)
        case 'date-time':
          return formatDateTime(props.value)
        default:
          return props.value as string
      }
  }
}

watch(
  [() => props, isInitialLoading],
  async () => {
    valueAsString.value = await calculateStringValue()
  },
  { deep: true, immediate: true },
)
</script>

<style scoped></style>
